import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const PINK = '#e8547a'
const LIGHT = '#fdeef3'
const BG = '#f8f9fa'
const BORDER = '#e3e5e8'
const GREEN = '#1d9e75'

const DEAL_TYPES = [
  { value: 'promo', label: '📣 Promotion', desc: 'Discount or limited-time offer (e.g. 20% off Mother\'s Day)' },
  { value: 'redeem', label: '🎁 Points Reward', desc: 'Something clients can redeem with their points' },
  { value: 'bonus_points', label: '⭐ Bonus Points', desc: 'Extra points for doing something (e.g. book a specific day)' },
]

export default function PortalManager() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [stats, setStats] = useState({ total_clients: 0, portal_clients: 0, avg_points: 0 })

  const blankDeal = { title: '', description: '', deal_type: 'promo', points_cost: '', expires_at: '', is_active: true }
  const [form, setForm] = useState(blankDeal)
  const [editId, setEditId] = useState(null)

  useEffect(() => { loadAll() }, [])

  async function loadAll() {
    setLoading(true)
    const [{ data: d }, { data: accts }, { data: clients }] = await Promise.all([
      supabase.from('deals').select('*').order('created_at', { ascending: false }),
      supabase.from('client_accounts').select('points'),
      supabase.from('clients').select('id', { count: 'exact', head: true }),
    ])
    setDeals(d || [])
    const portalClients = accts?.length || 0
    const avgPts = accts?.length ? Math.round(accts.reduce((a, c) => a + (c.points || 0), 0) / accts.length) : 0
    setStats({ total_clients: clients?.length || 0, portal_clients: portalClients, avg_points: avgPts })
    setLoading(false)
  }

  async function save() {
    if (!form.title.trim()) { alert('Deal needs a title.'); return }
    setSaving(true)
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      deal_type: form.deal_type,
      points_cost: form.points_cost ? parseInt(form.points_cost) : null,
      expires_at: form.expires_at || null,
      is_active: form.is_active,
    }
    if (editId) {
      await supabase.from('deals').update(payload).eq('id', editId)
    } else {
      await supabase.from('deals').insert(payload)
    }
    setSaving(false)
    setShowForm(false)
    setForm(blankDeal)
    setEditId(null)
    loadAll()
  }

  async function toggle(deal) {
    await supabase.from('deals').update({ is_active: !deal.is_active }).eq('id', deal.id)
    loadAll()
  }

  async function deleteDeal(id) {
    if (!window.confirm('Delete this deal?')) return
    await supabase.from('deals').delete().eq('id', id)
    loadAll()
  }

  function edit(deal) {
    setForm({
      title: deal.title,
      description: deal.description || '',
      deal_type: deal.deal_type || 'promo',
      points_cost: deal.points_cost || '',
      expires_at: deal.expires_at ? deal.expires_at.split('T')[0] : '',
      is_active: deal.is_active,
    })
    setEditId(deal.id)
    setShowForm(true)
  }

  const s = {
    input: { width: '100%', padding: '10px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 9, fontSize: 14, background: BG, color: '#202124', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' },
    label: { fontSize: 11, color: '#80868b', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block' },
    btn: { padding: '9px 18px', borderRadius: 9, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  }

  return (
    <div style={{ padding: '20px 24px', overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>📲 Client Portal Manager</div>
          <div style={{ fontSize: 12, color: '#80868b' }}>Manage deals, promos & rewards visible to clients</div>
        </div>
        <button style={{ ...s.btn, background: PINK, color: '#fff' }}
          onClick={() => { setForm(blankDeal); setEditId(null); setShowForm(true) }}>
          + New deal
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          ['👥', stats.portal_clients, 'Portal users', `of ${stats.total_clients} clients`],
          ['✨', stats.avg_points, 'Avg points', 'per client'],
          ['🎁', deals.filter(d => d.is_active).length, 'Active deals', 'showing in portal'],
        ].map(([icon, val, label, sub]) => (
          <div key={label} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '14px' }}>
            <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#202124' }}>{val}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#5f6368', marginTop: 2 }}>{label}</div>
            <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* New/Edit form */}
      {showForm && (
        <div style={{ background: '#fff', border: `2px solid ${PINK}`, borderRadius: 14, padding: '20px', marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{editId ? '✏️ Edit deal' : '✨ New deal / promo'}</div>

          {/* Type picker */}
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>Deal type</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {DEAL_TYPES.map(t => (
                <label key={t.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', border: `1.5px solid ${form.deal_type === t.value ? PINK : BORDER}`, borderRadius: 10, cursor: 'pointer', background: form.deal_type === t.value ? LIGHT : '#fff' }}>
                  <input type="radio" name="deal_type" value={t.value} checked={form.deal_type === t.value}
                    onChange={e => setForm(f => ({ ...f, deal_type: e.target.value }))} style={{ marginTop: 2, accentColor: PINK }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#202124' }}>{t.label}</div>
                    <div style={{ fontSize: 11, color: '#80868b', marginTop: 2 }}>{t.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={s.label}>Title *</label>
              <input style={s.input} placeholder={
                form.deal_type === 'promo' ? 'e.g. 20% off for Mother\'s Day 🌸' :
                form.deal_type === 'redeem' ? 'e.g. Free add-on room' :
                'e.g. +5 pts if you book this Thursday!'
              } value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={s.label}>Description (optional)</label>
              <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }}
                placeholder="Extra details clients will see..."
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            {(form.deal_type === 'redeem') && (
              <div>
                <label style={s.label}>Points cost</label>
                <input style={s.input} type="number" placeholder="e.g. 25" value={form.points_cost}
                  onChange={e => setForm(f => ({ ...f, points_cost: e.target.value }))} />
              </div>
            )}
            <div>
              <label style={s.label}>Expires (optional)</label>
              <input style={s.input} type="date" value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...s.btn, background: PINK, color: '#fff', flex: 1 }} onClick={save} disabled={saving}>
              {saving ? 'Saving...' : editId ? '💾 Update deal' : '🚀 Publish to portal'}
            </button>
            <button style={{ ...s.btn, background: BG, color: '#80868b' }} onClick={() => { setShowForm(false); setEditId(null); setForm(blankDeal) }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Deals list */}
      <div style={{ fontSize: 12, color: '#80868b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
        All deals ({deals.length})
      </div>

      {loading && <div style={{ color: '#9aa0a6', fontSize: 13 }}>Loading...</div>}

      {!loading && deals.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9aa0a6', fontSize: 13 }}>
          No deals yet — create one to show it in the client portal!
        </div>
      )}

      {deals.map(d => (
        <div key={d.id} style={{ background: '#fff', border: `1px solid ${d.is_active ? BORDER : '#f0f0f0'}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10, opacity: d.is_active ? 1 : .55 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
            <div style={{ flex: 1, paddingRight: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{d.title}</span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: d.deal_type === 'promo' ? '#fff3e0' : d.deal_type === 'bonus_points' ? '#e8f5e9' : LIGHT, color: d.deal_type === 'promo' ? '#e65100' : d.deal_type === 'bonus_points' ? '#2e7d32' : PINK }}>
                  {DEAL_TYPES.find(t => t.value === d.deal_type)?.label || d.deal_type}
                </span>
                {!d.is_active && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: '#f1f3f4', color: '#9aa0a6', fontWeight: 700 }}>HIDDEN</span>}
              </div>
              {d.description && <div style={{ fontSize: 12, color: '#80868b', marginTop: 4, lineHeight: 1.5 }}>{d.description}</div>}
              <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: '#9aa0a6' }}>
                {d.points_cost && <span>💎 {d.points_cost} pts to redeem</span>}
                {d.expires_at && <span>⏰ Expires {new Date(d.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button style={{ ...s.btn, padding: '6px 12px', background: d.is_active ? '#f1f3f4' : '#f0fdf4', color: d.is_active ? '#80868b' : GREEN, fontSize: 12 }}
                onClick={() => toggle(d)}>
                {d.is_active ? 'Hide' : 'Show'}
              </button>
              <button style={{ ...s.btn, padding: '6px 12px', background: LIGHT, color: PINK, fontSize: 12 }} onClick={() => edit(d)}>Edit</button>
              <button style={{ ...s.btn, padding: '6px 10px', background: '#fff0f0', color: '#e57373', fontSize: 12 }} onClick={() => deleteDeal(d.id)}>✕</button>
            </div>
          </div>
        </div>
      ))}

      {/* Portal link */}
      <div style={{ marginTop: 28, padding: '16px', background: BG, border: `1px solid ${BORDER}`, borderRadius: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#80868b', marginBottom: 8 }}>📲 Client portal link</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, fontSize: 12, color: PINK, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 12px', wordBreak: 'break-all' }}>
            {window.location.origin}?client=1
          </div>
          <button style={{ ...s.btn, background: PINK, color: '#fff', padding: '8px 14px', fontSize: 12, flexShrink: 0 }}
            onClick={() => navigator.clipboard?.writeText(`${window.location.origin}?client=1`)}>
            Copy
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 8 }}>Share this link with clients so they can create an account and access their portal.</div>
      </div>
    </div>
  )
}
